// «Novedades de esta versión» — the per-build release notes written on Satori Core (shared/API.md §11.3),
// shown once after an update, offered with the "new version" banner and listed from Configuración.
// Port of the Windows `Update/ReleaseNotes.cs` (twins: Android update/ReleaseNotes.kt, Apple
// Sources/Models/ReleaseNotes.swift) — same names, same rules, same tests.
//
// Web specifics (docs/ai/domains/web.md «Release notes»): the web has no updater token and its CSP is
// `connect-src 'self'`, so the request goes to the chat domain's own `/core-api/app-update/…` path
// and its proxy adds the token. The web rides the Mac's release train (it ships with every Mac/Android
// release), so it asks for `platform=macos` with the Android/Mac versionCode stamped at build time.
import { t, tf } from '../i18n/i18n';

export type ReleaseNoteKind = 'feature' | 'improvement' | 'fix';

export interface ReleaseNoteItem {
  kind: ReleaseNoteKind;
  text: string;
}

/** One published build's notes. `date` is the server's d/m/Y, shown as given. */
export interface AppReleaseNotes {
  versionName: string;
  versionCode: number;
  title: string | null;
  date: string | null;
  items: ReleaseNoteItem[];
}

export const RELEASE_NOTES_PATH = '/core-api/app-update/satori-chat/release-notes';
/** The platform whose notes the web shows (it ships on the Mac's release train). */
export const RELEASE_NOTES_PLATFORM = 'macos';

function str(o: Record<string, unknown>, name: string): string | null {
  const v = o[name];
  if (typeof v !== 'string') return null;
  const s = v.trim();
  return s ? s : null;
}

function int(o: Record<string, unknown>, name: string): number | null {
  const v = o[name];
  if (typeof v === 'number' && Number.isInteger(v)) return v;
  if (typeof v === 'string' && /^-?\d+$/.test(v.trim())) return Number(v.trim());
  return null;
}

export const ReleaseNotesDecoding = {
  kind(wire: string | null | undefined): ReleaseNoteKind {
    return wire === 'feature' ? 'feature' : wire === 'fix' ? 'fix' : 'improvement';
  },

  /** A `releases` / `release_notes` array: entries without a positive build or a version name, blank
   *  items, and releases left with neither items nor a title are dropped; one entry per build; newest first. */
  parseArray(array: unknown): AppReleaseNotes[] {
    if (!Array.isArray(array)) return [];
    const byCode = new Map<number, AppReleaseNotes>();
    for (const entry of array) {
      if (!entry || typeof entry !== 'object' || Array.isArray(entry)) continue;
      const o = entry as Record<string, unknown>;
      const code = int(o, 'version_code');
      const name = str(o, 'version_name');
      if (code === null || code <= 0 || name === null) continue;
      const items: ReleaseNoteItem[] = [];
      if (Array.isArray(o.items)) {
        for (const item of o.items) {
          if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
          const text = str(item as Record<string, unknown>, 'text');
          if (text === null) continue;
          items.push({ kind: ReleaseNotesDecoding.kind(str(item as Record<string, unknown>, 'type')), text });
        }
      }
      const title = str(o, 'title');
      if (!items.length && title === null) continue;
      if (!byCode.has(code)) byCode.set(code, { versionName: name, versionCode: code, title, date: str(o, 'date'), items });
    }
    return [...byCode.values()].sort((a, b) => b.versionCode - a.versionCode);
  },

  /** The endpoint's answer `{success, releases:[…]}`; null = the fetch failed (garbage, `success:false`,
   *  no `releases`) — never "there is nothing new". */
  parseResponse(json: unknown): AppReleaseNotes[] | null {
    let root = json;
    if (typeof root === 'string') {
      if (!root.trim()) return null;
      try {
        root = JSON.parse(root);
      } catch {
        return null;
      }
    }
    if (!root || typeof root !== 'object' || Array.isArray(root)) return null;
    const o = root as Record<string, unknown>;
    if (o.success === false) return null;
    if (!Array.isArray(o.releases)) return null;
    return ReleaseNotesDecoding.parseArray(o.releases);
  },
};

/** Builds (since, until] the after-update fetch asks for. */
export interface ReleaseNotesRange {
  since: number;
  until: number;
}

export const ReleaseNotesSchedule = {
  /** How many releases the history (and the after-update sheet) asks for. */
  historyLimit: 20,

  /** What to fetch after an update: nothing when this build's notes were already looked up (or the build
   *  is unknown); otherwise everything after the last build looked up — or just this build on a first
   *  run with the feature. */
  pendingRange(lastShownBuild: number | null, currentBuild: number): ReleaseNotesRange | null {
    if (!(currentBuild > 0)) return null;
    if (lastShownBuild !== null && lastShownBuild >= currentBuild) return null;
    return { since: lastShownBuild ?? currentBuild - 1, until: currentBuild };
  },

  /** A version subheading per release only when the sheet holds more than one. */
  showsVersionSubheading(releases: AppReleaseNotes[]): boolean {
    return releases.length > 1;
  },

  heading(release: AppReleaseNotes): string {
    return release.title && release.title.trim() ? release.title : tf('releaseNotes.releaseFallbackTitle', release.versionName);
  },

  /** The line under the heading: "Versión X · fecha" when there are several releases, else the date. */
  caption(release: AppReleaseNotes, showVersion: boolean): string | null {
    if (showVersion) return release.date ? tf('releaseNotes.versionLine', release.versionName, release.date) : tf('releaseNotes.versionOnly', release.versionName);
    return release.date;
  },

  kindLabel(kind: ReleaseNoteKind): string {
    return kind === 'feature' ? t('releaseNotes.kind.feature') : kind === 'fix' ? t('releaseNotes.kind.fix') : t('releaseNotes.kind.improvement');
  },

  /** The request's query: `since` exclusive, `until` inclusive (omitted = newest), limit clamped 1…50. */
  query(since: number, until: number | null, limit: number): string {
    const q = new URLSearchParams({ platform: RELEASE_NOTES_PLATFORM, since: String(since), limit: String(Math.max(1, Math.min(50, limit))) });
    if (until !== null) q.set('until', String(until));
    return q.toString();
  },
};
